import React, { useEffect, useState } from 'react';
import { List, ListItem, ListItemAvatar, ListItemText, Avatar, Divider, Typography, Box, Chip, useTheme, useMediaQuery } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { useChatContext } from '../hooks/ChatProvider';

const ChatList = () => {
  const [chats, setChats] = useState([]);

  const { selectedUser, setSelectedUser } = useChatContext();

  // Enhanced breakpoints for better responsiveness
  const theme = useTheme();
  const isXsScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMdScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isLgScreen = useMediaQuery(theme.breakpoints.down('lg'));

  const handleClick = (user) => {
    setSelectedUser(user);
    localStorage.setItem("lastTopicSelected", JSON.stringify(user.topic));
  };

  const parseMessage = (message) => {
    // Replace markdown-like syntax with HTML tags
    const formattedMessage = message ?? ""
        .replace(/_([^_]+)_/g, '<i>$1</i>') // Italic: _text_
        .replace(/\*([^\*]+)\*/g, '<b>$1</b>') // Bold: *text*
        .replace(/~([^~]+)~/g, '<s>$1</s>'); // Strikethrough: ~text~

    return formattedMessage;
  };

  const adminSubscriberCode = localStorage.getItem("code");

  useEffect(() => {
    // Initialize with sample dummy chats
    // setChats([
    //   {
    //     id: 1,
    //     topic: "topic_1",
    //     sender: "919876543210",  // here sender == sender 
    //     name: "John Doe",
    //     message: "Hey, I need some help with my order.",
    //     supportUser: null,
    //   },
    //   {
    //     id: 2,
    //     topic: "topic_2",
    //     sender: "919812345678",
    //     name: "Jane Smith",
    //     message: "Can you provide an update on my request?",
    //     supportUser: "support_agent_1",
    //   },
    //   {
    //     id: 3,
    //     topic: "topic_3",
    //     sender: "919711122233",
    //     name: "Michael Johnson",
    //     message: "Is there a way to track my delivery?",
    //     supportUser: null,
    //   },
    // ]);

    const tenant = localStorage.getItem("tenant");
    // Dev URL
    // const socketURL = process.env.NODE_ENV === 'production' ? `wss://dev.chatbot.bizcollab360.com/ws/chat/list/admin/${adminSubscriberCode}/?tenant=${tenant}` : `ws://localhost:8000/ws/chat/list/admin/${adminSubscriberCode}/?tenant=${tenant}`;
    const socketURL = process.env.NODE_ENV === 'production' ? `wss://app.mybotbridge.com/ws/chat/list/admin/${adminSubscriberCode}/?tenant=${tenant}` : `ws://localhost:8000/ws/chat/list/admin/${adminSubscriberCode}/?tenant=${tenant}`;
    // const baseWSUrl = process.env.REACT_APP_WS_BASE_URL || 'ws://localhost:8000';
    // const socketURL = `${baseWSUrl}/ws/chat/list/admin/${adminSubscriberCode}/`;
    
    const socket = new WebSocket(socketURL);
  
    socket.onmessage = (e) => {
      const data = JSON.parse(e.data);
      const { topic, publisher, last_msg, support_subscriber, notification_type, tenant_subdomain } = data;
      const { message } = last_msg || {};
      const { phone, name } = publisher || {};  // {phone: "919619187415", code: "KEfTvHFjMFrbK9f8VktDZ2jRA5Ts85_psRjqRZqthz4", name: "Rakhi Patil"}
      
      setChats((prevChats) => {
        let updatedChats = [...prevChats];
        let selectedUserChanged = false;
  
        if (notification_type === "topics.remove") {
          updatedChats = updatedChats.filter(chat => {
            if (chat.topic === topic && selectedUser?.topic === topic) {
              selectedUserChanged = true;
            }
            return chat.topic !== topic;
          });
        } else if (notification_type === "topics.update" || notification_type === undefined) {
          const existingChatIndex = updatedChats.findIndex(chat => chat.topic === topic);
          if (existingChatIndex !== -1) {
            updatedChats[existingChatIndex] = {
              ...updatedChats[existingChatIndex],
              message,
              topic,
              supportUser: support_subscriber,
              name,
              tenant_subdomain
            };
          } else {
            if (tenant_subdomain === tenant)
            updatedChats.push({
              id: prevChats.length + 1,
              topic,
              phone,
              name,
              message,
              supportUser: support_subscriber,
            });
          }
        } else if (notification_type === "topics.update.support_subscriber") {
          const existingChatIndex = updatedChats.findIndex(chat => chat.topic === topic);

          if (existingChatIndex !== -1 && tenant_subdomain === tenant) {
            updatedChats[existingChatIndex] = {
              ...updatedChats[existingChatIndex],
              supportUser: support_subscriber,
              name,
              tenant_subdomain,
            };

            if (selectedUser && selectedUser.topic === topic) {
              setSelectedUser({
                ...selectedUser,
                supportUser: support_subscriber,
              });
            }
          }
        }
  
        if (selectedUserChanged || !selectedUser) {
          const nextUser = updatedChats[0] || null;
          setSelectedUser(nextUser);
          if (nextUser) {
            localStorage.setItem("lastTopicSelected", JSON.stringify(nextUser.topic));
          } else {
            localStorage.removeItem("lastTopicSelected");
          }
        }
  
        return updatedChats;
      });
    };
  
    return () => socket.close();
  }, [selectedUser, setSelectedUser]);
  
  // Function to truncate long names for better display
  const truncateName = (name) => {
    if (!name) return '';
    
    if (isXsScreen && name.length > 10) {
      return name.substring(0, 8) + '...';
    } else if (isMdScreen && name.length > 12) {
      return name.substring(0, 10) + '...';
    } else if (isLgScreen && name.length > 15) {
      return name.substring(0, 13) + '...';
    }
    
    return name;
  };
  
  return (
    // Container that will just house the list without the duplicate header
    // Note: The "Chats" header is already in the parent component
    <Box
      sx={{
        // Use flex layout but NOT absolute positioning
        display: 'flex',
        flexDirection: 'column',
        height: '100%', 
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        // Set a max-height that matches the blue sidebar's constraints
        maxHeight: 'calc(100vh - 190px)', // Account for header, footer, and other UI elements
        // Apply responsive width constraints
        ...(isXsScreen && {
          maxWidth: '180px',
        }),
        ...(isMdScreen && !isXsScreen && {
          maxWidth: '220px',
        }),
      }}
    >
      {/* Scrollable chat list container */}
      <Box
        sx={{
          // Use both height and maxHeight to ensure proper containment
          height: '100%',
          maxHeight: '90%', 
          overflowY: 'auto',
          overflowX: 'hidden',
          // Make sure this list doesn't grow beyond its container
          flex: '1 1 auto',
          // Prevent content from overflowing the blue sidebar
          position: 'relative',
          scrollbarWidth: 'thin',
          scrollbarColor: '#555 transparent',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255, 255, 255, 0.3)',
            borderRadius: '10px',
            transition: 'background 0.3s ease-in-out',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'rgba(255, 255, 255, 0.6)',
          },
        }}
      >
        <List sx={{ 
          width: '100%', 
          padding: 0,
          margin: 0, // Ensure no margin pushes content outside container
          boxSizing: 'border-box',
        }}>
          {chats.length > 0 ? (
            chats.map((chat, index) => (
              <React.Fragment key={chat.id}>
                <ListItem 
                  button 
                  onClick={() => handleClick(chat)}
                  sx={{ 
                    width: '100%',
                    maxWidth: '100%',
                    padding: isXsScreen ? '8px 6px' : isMdScreen ? '8px 8px' : '8px 16px',
                    boxSizing: 'border-box',
                    position: 'relative',
                  }}
                >
                  <Box sx={{ 
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    width: '100%',
                    position: 'relative',
                  }}>
                    <ListItemAvatar sx={{ 
                      minWidth: isXsScreen ? '32px' : isMdScreen ? '40px' : '56px',
                      marginRight: isXsScreen ? '4px' : isMdScreen ? '6px' : '8px',
                    }}>
                      <Avatar sx={{ 
                        width: isXsScreen ? '24px' : isMdScreen ? '32px' : '40px',
                        height: isXsScreen ? '24px' : isMdScreen ? '32px' : '40px',
                      }}>
                        <PersonIcon fontSize={isXsScreen ? 'small' : 'medium'} />
                      </Avatar>
                    </ListItemAvatar>
                    
                    <Box sx={{ 
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'hidden',
                      width: chat.supportUser === null ? 'calc(100% - 50px)' : '100%',
                    }}>
                      <Typography
                        variant={isXsScreen ? "caption" : isMdScreen ? "body2" : "body1"}
                        noWrap
                        sx={{ 
                          display: 'block',
                          fontWeight: 'medium',
                          lineHeight: 1.2,
                          marginBottom: '2px',
                        }}
                      >
                        {truncateName(chat.name)}
                      </Typography>
                      
                      <Typography 
                        component="span" 
                        variant={isXsScreen ? "caption" : "body2"}
                        noWrap
                        sx={{ 
                          display: 'block',
                          color: '#d9d9d9',
                          fontSize: isXsScreen ? '0.65rem' : isMdScreen ? '0.75rem' : '0.875rem',
                        }}
                      >
                        {isXsScreen || isMdScreen 
                          ? `+${chat.phone}` 
                          : `+${chat.phone}`
                        }
                      </Typography>
                    </Box>
                    
                    {chat.supportUser === null && (
                      <Chip 
                        label="new" 
                        color="error" 
                        size="small"
                        sx={{ 
                          position: 'absolute',
                          right: isXsScreen ? '2px' : '8px',
                          top: isXsScreen ? '4px' : '8px',
                          height: isXsScreen ? '16px' : isMdScreen ? '20px' : '24px',
                          fontSize: isXsScreen ? '0.6rem' : isMdScreen ? '0.65rem' : '0.75rem',
                          '& .MuiChip-label': {
                            padding: isXsScreen ? '0 4px' : isMdScreen ? '0 6px' : '0 8px',
                          },
                          minWidth: isXsScreen ? '28px' : '32px',
                        }}
                      />
                    )}
                  </Box>
                </ListItem>
                {index < chats.length - 1 && <Divider component="li" variant="middle" />}
              </React.Fragment>
            ))
          ) : (
            <Box display="flex" flexDirection="column" alignItems="center" py={5} color="textSecondary">
              <ChatBubbleOutlineIcon fontSize="large" />
              <Typography variant="h6" mt={2}>
                No Chats Available
              </Typography>
              <Typography variant="body2">
                Start a new conversation to see your chats here.
              </Typography>
            </Box>
          )}
        </List>
      </Box>
    </Box>
  );
};

export default ChatList;
